import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, CheckCircle2, Loader2, RotateCw, ArrowLeft } from 'lucide-react';

const OTP_LENGTH = 6;

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp, resendVerification } = useAuth();
  const inputsRef = useRef([]);

  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const e = params.get('email') || '';
    setEmail(e);
    // Focus first input
    setTimeout(() => inputsRef.current[0]?.focus(), 100);
  }, [location.search]);

  // Cooldown countdown for "Resend"
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleChange = (idx, value) => {
    const v = value.replace(/[^0-9]/g, '').slice(0, 1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setError('');
    if (v && idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    } else if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/[^0-9]/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    setError('');
    inputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleSubmit = async (override) => {
    const otp = (override || digits.join('')).trim();
    if (otp.length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH}-digit code from your email.`);
      return;
    }
    if (!email) {
      setError('Email missing — go back to login and start over.');
      return;
    }
    setSubmitting(true);
    setError('');
    const r = await verifyOtp(email, otp);
    setSubmitting(false);
    if (r.success) {
      setSuccess('Email verified! Taking you in…');
      setTimeout(() => navigate('/dashboard', { replace: true }), 900);
    } else {
      setError(r.error || 'Verification failed.');
    }
  };

  const handleResend = async () => {
    if (!email) { setError('Email missing.'); return; }
    setResending(true);
    setError('');
    const r = await resendVerification(email);
    setResending(false);
    if (r.success) {
      setSuccess('A fresh OTP has been sent to your email.');
      setCooldown(30);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(r.error || 'Failed to resend.');
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)' }}
    >
      <div className="w-full max-w-md">
        <div className="glass-card p-8 shadow-xl" data-testid="verify-otp-card">
          <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] mb-4">
            <Mail className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-black text-center text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="verify-otp-title">
            Enter the OTP
          </h1>
          <p className="mt-1 text-sm text-center text-[#6B7280]" data-testid="verify-otp-subtitle">
            We sent a 6-digit code to {email ? <strong className="text-[#111827]">{email}</strong> : 'your email'}.
          </p>

          {success && (
            <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2" data-testid="verify-otp-success">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {success}
            </div>
          )}
          {error && (
            <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="verify-otp-error">
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-black border-2 border-slate-200 rounded-2xl focus:border-[#0EA5E9] outline-none transition bg-white"
                data-testid={`otp-digit-${i}`}
              />
            ))}
          </div>

          <button
            onClick={() => handleSubmit()}
            disabled={submitting || digits.join('').length !== OTP_LENGTH}
            className="mt-6 w-full px-6 py-4 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="verify-otp-submit"
          >
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Verifying…</> : 'Verify email'}
          </button>

          <div className="mt-5 flex items-center justify-between text-sm">
            <Link to="/login" className="inline-flex items-center gap-1 text-[#6B7280] hover:text-[#0EA5E9] transition" data-testid="verify-otp-back">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
            <button
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              className="inline-flex items-center gap-1 text-[#0EA5E9] font-bold hover:underline disabled:opacity-50 disabled:no-underline disabled:text-[#9CA3AF]"
              data-testid="verify-otp-resend"
            >
              <RotateCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
              {cooldown > 0 ? `Resend in ${cooldown}s` : (resending ? 'Sending…' : 'Resend OTP')}
            </button>
          </div>

          <p className="mt-5 text-xs text-center text-[#9CA3AF]">
            The code expires in 10 minutes. Check your spam folder if you don't see it.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
